import { get } from 'lodash';
import LoginApi from 'client/api/LoginApi';
import LoginConsts from 'client/util/Constants/LoginConstants';

const { admin } = LoginConsts.clientType;

class AdminLoginApi extends LoginApi {
  /**
   * Primary authentication for an admin.
   * @param  {Object} payload [email, password]
   * @return {Object} [returns the promise from the axios request]
   */
  login(payload) {
    const path = LoginConsts.authURLs[admin];

    return this.axios
      .post(path, payload)
      .catch(error => this.retryIfInvalidCsrf(error, path, payload));
  }

  /**
   * MFA authentication for an admin
   * @param {Object} payload [otp: token to be verified]
   * @return {Object} [returns the promise from the axios request]
   */
  verifyTotp(payload) {
    const path = '/auth/mfa';

    return this.axios
      .post(path, payload)
      .catch(error => this.retryIfInvalidCsrf(error, path, payload));
  }

  /**
   * Admin registration verification when admin signs up via Google
   * @param  {Object} payload [containes user registration questions]
   * @return {Object} [returns the promise from the axios request]
   */
  newOrgVerification(payload) {
    return this.axios.post('/login/new-org-verification', payload);
  }

  /**
  * Requests for a password change. Upon success the admin will
  * receive an email.
  * @param  {Object} payload [contains email that will receive the email]
  * @return {Object} [returns the promise from the axios request]
  */
  requestPasswordReset(payload) {
    return this.axios.post('/passwordreset', payload);
  }

  /**
  * Requests for a TOTP change. Upon success the admin will
  * receive an email.
  * @param  {Object} payload [contains email that will receive the email]
  * @return {Object} [returns the promise from the axios request]
  */
  requestTOTPReset() {
    return this.axios.post('/api/users/resettotp/self');
  }

  /**
  * Resets an admin's password. If an admin has MFA enabled, the admin will be required
  * to verify with a new TOTP key. A request to this API endpoint will return an error
  * and a payload of a TOTP Key if the user has MFA enabled. The admin must use the new
  * TOTP Key to verify with an OTP.
  * @param  {ResetPassword} ResetPassword
  * @param  {String} ResetPassword.password [The user's password]
  * @param  {String} ResetPassword.confirm [The user's password again]
  * @param  {String} ResetPassword.confirmationCode [The request key generated in the email]
  * @param  {Boolean} ResetPassword.license [Whether the user has accepted the Terms of Use]
  * @param  {String} ResetPassword.otp One-time password used to confirm new TOTP key
  * @return {Promise} [returns Axios promise]
  */
  resetPassword(resetPasswordPayload) {
    return this.axios.post('/passwordreset/verify', resetPasswordPayload);
  }

  /**
  * Resets an admin's password. If an admin has MFA enabled, the admin will be required
  * to verify with a new TOTP key. A request to this API endpoint will return an error
  * and a payload of a TOTP Key if the user has MFA enabled. The admin must use the new
  * TOTP Key to verify with an OTP.
  * @param  {ResetPassword} ResetPassword
  * @param  {String} ResetPassword.password [The user's password]
  * @param  {String} ResetPassword.confirmationCode [The request key generated in the email]
  * @param  {String} ResetPassword.otp One-time password used to confirm new TOTP key
  * @return {Promise} [returns Axios promise]
  */
  verifyMfaResetTOTP(resetPasswordPayload) {
    return this.axios.post('/totpsetup/confirm', resetPasswordPayload);
  }

  /**
   * Fetch a new CSRF token when the invalid CSRF error is received in the
   * response. The admin user likely got logged out after 10 minutes of
   * inactivity, and then remained on the login page for more than 10 minutes
   * so that the CSRF token associated with their new session is now invalid.
   */
  retryIfInvalidCsrf(error, path, payload) {
    const { invalidCsrf } = LoginConsts.responseMessages;
    const message = get(error, 'response.data.message', '');

    if (message === invalidCsrf) {
      this.clearCsrfToken();
      return this.axios.post(path, payload);
    }

    throw error;
  }

  /**
  * Confirms the validity of a given confirmation key
  * @param  {String} key [the confirmation key for resetting the password]
  * @return {Object} [returns the promise from the axios request]
  */
  validateKey(key) {
    return this.axios.get(`/passwordreset/${key}`);
  }

  /**
  * Confirms the validity of a given confirmation key for resetting totp
  * @param  {String} key [the confirmation key for resetting the password]
  * @return {Object} [returns the promise from the axios request]
  */
  validateTotpKey(key) {
    return this.axios.get(`/totpsetup/${key}`);
  }
}

export default new AdminLoginApi();
